import React, { useState, useEffect } from 'react';
import { CardNumberElement, CardExpiryElement, CardCvcElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { useNavigate } from 'react-router-dom';
import styles from './StripeCheckoutForm.module.css';
import { authenticatedFetch } from '../../utils/authHelpers';

const CARD_ELEMENT_OPTIONS = {
  style: {
    base: {
      fontSize: '16px',
      color: '#424770',
      '::placeholder': {
        color: '#aab7c4',
      },
      backgroundColor: 'transparent',
    },
    invalid: {
      color: '#9e2146',
    },
  },
};

const StripeCheckoutForm = ({ selectedTier, onSuccess, onError, userEmail, userId, getPriceId, onCancel, isChangePlanMode = false, currentUserTier = null }) => {
  const stripe = useStripe();
  const elements = useElements();
  const navigate = useNavigate();
  const [error, setError] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [succeeded, setSucceeded] = useState(false);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setProcessing(true);
    setError(null);

    if (!stripe || !elements || !selectedTier) {
      setProcessing(false);
      return;
    }

    try {
      // If change-plan mode: schedule change instead of collecting card details
      if (isChangePlanMode) {
        // Decide changeType based on rank ordering
        const rank = { individual: 1, team: 2, enterprise: 3 };
        const currentRank = currentUserTier ? rank[currentUserTier.toLowerCase()] : null;
        const targetRank = rank[selectedTier.name.toLowerCase()];
        const changeType = currentRank && targetRank && targetRank > currentRank ? 'upgrade' : 'downgrade';

        const response = await authenticatedFetch('/api/stripe/change-plan', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ newTier: selectedTier.name.toLowerCase(), changeType })
        });
        const result = await response.json();
        if (!response.ok) {
          throw new Error(result.error || 'Failed to schedule plan change');
        }

        setSucceeded(true);
        setProcessing(false);
        onSuccess && onSuccess();
        setTimeout(() => {
          navigate('/settings');
        }, 1000);
        return;
      }

      // 1. Create payment method from card elements
      const { error: paymentMethodError, paymentMethod } = await stripe.createPaymentMethod({
        type: 'card',
        card: elements.getElement(CardNumberElement),
        billing_details: {
          email: userEmail,
        },
      });

      if (paymentMethodError) {
        setError(paymentMethodError.message);
        setProcessing(false);
        return;
      }

      // 2. Call our backend to create subscription
      const response = await fetch('/api/stripe/create-subscription', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userId: userId,
          email: userEmail,
          subscriptionTier: selectedTier.name.toLowerCase(),
          paymentMethodId: paymentMethod.id,
        }),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Failed to create subscription');
      }

      // 3. Handle the response
      if (result.clientSecret) {
        // If we got a client secret, confirm the payment
        const { error: confirmError } = await stripe.confirmCardPayment(result.clientSecret);
        
        if (confirmError) {
          setError(confirmError.message);
          setProcessing(false);
          return;
        }
      }

      // 4. Success!
      setSucceeded(true);
      setProcessing(false);
      onSuccess && onSuccess();
      
      // 5. Redirect to upload page after a short delay
      setTimeout(() => {
        navigate('/upload');
      }, 2000);

    } catch (err) {
      console.error('Subscription creation error:', err);
      setError(err.message || 'Payment failed. Please try again.');
      setProcessing(false);
      onError && onError(err);
    }
  };

  const handleChange = (event) => {
    if (event.error) {
      setError(event.error.message);
    } else {
      setError(null);
    }
  };

  return (
    <div className={styles.checkoutForm}>
      {/* Header */}
      <div className={styles.formHeader}>
        <h3>{isChangePlanMode ? 'Change Plan' : 'Payment Information'}</h3>
        <p>
          {selectedTier 
            ? isChangePlanMode
              ? `You are changing to the ${selectedTier.name} plan`
              : `You are subscribing to the ${selectedTier.name} plan for ${selectedTier.price}/month`
            : 'Select a plan to get started'
          }
        </p>
      </div>

      {/* First Divider */}
      <hr className={styles.divider} />

      {/* Credit Card Form */}
      <form onSubmit={handleSubmit} className={styles.cardForm}>
        {/* Card Number */}
        <div
          className={styles.fieldContainer}
          style={{ opacity: isChangePlanMode ? 0.5 : 1, pointerEvents: isChangePlanMode ? 'none' : 'auto' }}
          aria-disabled={isChangePlanMode}
        >
          <label className={styles.fieldLabel}>Card Number</label>
          <div className={styles.cardElementContainer}>
            <CardNumberElement
              id="card-number"
              options={CARD_ELEMENT_OPTIONS}
              onChange={handleChange}
              disabled={isChangePlanMode}
            />
          </div>
        </div>

        {/* Expiry Date and CVC Row */}
        <div className={styles.cardRow}>
          <div
            className={styles.fieldContainer}
            style={{ opacity: isChangePlanMode ? 0.5 : 1, pointerEvents: isChangePlanMode ? 'none' : 'auto' }}
            aria-disabled={isChangePlanMode}
          >
            <label className={styles.fieldLabel}>Expiry Date</label>
            <div className={styles.cardElementContainer}>
              <CardExpiryElement
                id="card-expiry"
                options={CARD_ELEMENT_OPTIONS}
                onChange={handleChange}
                disabled={isChangePlanMode}
              />
            </div>
          </div>
          
          <div
            className={styles.fieldContainer}
            style={{ opacity: isChangePlanMode ? 0.5 : 1, pointerEvents: isChangePlanMode ? 'none' : 'auto' }}
            aria-disabled={isChangePlanMode}
          >
            <label className={styles.fieldLabel}>CVC</label>
            <div className={styles.cardElementContainer}>
              <CardCvcElement
                id="card-cvc"
                options={CARD_ELEMENT_OPTIONS}
                onChange={handleChange}
                disabled={isChangePlanMode}
              />
            </div>
          </div>
        </div>

        {/* Error Display */}
        {error && (
          <div className={styles.errorMessage}>
            {error}
          </div>
        )}

        {/* Button + Cancel Link Grouped */}
        <div className={styles.buttonGroup}>
          <button
            type="submit"
            disabled={!stripe || processing || succeeded || !selectedTier}
            className={styles.submitButton}
          >
            {processing
              ? 'Processing...'
              : !selectedTier
                ? 'Select a plan first'
                : isChangePlanMode
                  ? 'Change Plan'
                  : `Subscribe for ${selectedTier.price}/month`}
          </button>
          <div className={styles.cancelLinkRow}>
            <a
              href="#"
              className={styles.cancelLink}
              onClick={e => {
                e.preventDefault();
                if (typeof onCancel === 'function') {
                  try {
                    onCancel();
                  } catch (err) {
                    navigate('/upload');
                  }
                } else {
                  navigate('/upload');
                }
              }}
            >
              Cancel
            </a>
          </div>
        </div>

        {/* Success Message */}
        {succeeded && (
          <div className={styles.successMessage}>
            Payment successful! Your subscription is now active.
          </div>
        )}
      </form>

      {/* Security Notice */}
      <div className={styles.securityNotice}>
        <p>🔒 Your payment information is secure and encrypted</p>
        {isChangePlanMode && (
          <p style={{ marginTop: '4px', color: '#666' }}>Card entry is not required for plan changes.</p>
        )}
      </div>
    </div>
  );
};

export default StripeCheckoutForm; 
